import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { getSourceAndLink } from "pages/FindSection/functions"
import { DataList } from "../types"

type ExportExcel = {
  data: DataList[]
  dateFilter: string
  companyName?: string
}

export const exportExcelFindFinance = async ({
  data,
  dateFilter
}: ExportExcel) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "DATE",
    "ID#",
    "SOURCE",
    "ACCOUNT",
    "ACCOUNT NAME",
    "MEMO",
    "DEBIT",
    "CREDIT",
    "JOB ID",
    "LOCATION"
  ]
  worksheet.views = [{ showGridLines: false }]

  worksheet.mergeCells(2, 1, 1, columns.length)

  // Title
  makeTitle(worksheet, dateFilter)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF8C308" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  makeRows(data, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `find-records-finance-${dateFilter}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

function makeTitle(worksheet: Worksheet, dateFilter: string) {
  const titleCell = worksheet.getCell(1, 2)
  titleCell.value = "FIND RECORDS FINANCE"
  titleCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }
  titleCell.font = { bold: true, size: 15 }

  worksheet.getColumn(2).width = 20

  const dateFilterCell = worksheet.addRow([`DATE: ${dateFilter}`])

  const currentRowIdx = worksheet.rowCount
  const endColumnIdx = worksheet.columnCount

  worksheet.mergeCells(currentRowIdx, 1, currentRowIdx + 1, endColumnIdx)

  dateFilterCell.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true
    }
    cell.font = { bold: true, size: 13 }
  })
}

function makeRows(data: ExportExcel["data"], worksheet: Worksheet) {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    itemRow = worksheet.addRow([
      index + 1,
      moment(item.transaction_date).format("DD/MM/YYYY"),
      item.trx_code,
      item.data_source ? getSourceAndLink(item.data_source, item)?.source : "-",
      `${item?.account.group_coa_code} - ${item?.account.coa_code}`,
      item.account.coa_name,
      item.memo,
      item.position === 1 && item.amount > 0 ? item.amount : "",
      item.position === 2 && item.amount > 0 ? item.amount : "",
      item.project.project_name,
      item.location.location_name
    ])

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })
  })

  const calculate = data.reduce(
    (acc, item) => {
      return {
        debit: acc.debit + (item.position === 1 ? item.amount : 0),
        credit: acc.credit + (item.position === 2 ? item.amount : 0)
      }
    },
    { debit: 0, credit: 0 }
  )

  // summary
  const calculateDebitCredit = worksheet.addRow(
    Array(7).fill("").concat([calculate.debit, calculate.credit, "#", "#"])
  )

  calculateDebitCredit.getCell(8).font = { bold: true }
  calculateDebitCredit.getCell(9).font = { bold: true }
  calculateDebitCredit.eachCell((cell) => {
    cell.border = cellBorder
  })
}
